import aiMentionsContentDefaults from "../content/aiMentionsContentDefaults";
import {
    AiMentionsChatDemoContent,
    AiMentionsContentValues,
    AiMentionsFinalCtaContent,
    AiMentionsHeroContent,
    AiMentionsProseContent,
    AiMentionsStepContent,
    AiMentionsTwoMinutesContent,
    AiMentionsWhatsappContent
} from "../models/AiMentionsContent";
import {createContentDraftStore} from "./contentDraftStore";
import useRevisionedContent, {RevisionedContentState} from "./useRevisionedContent";

export const AI_MENTIONS_CONTENT_URL = '/site-content/ai-mentions';
// Tags the draft envelope: an AI Mentions preview token can never be read as
// homepage content, nor the other way round.
export const AI_MENTIONS_CONTENT_TYPE = 'ai-mentions';
export const AI_MENTIONS_DRAFT_PREFIX = 'aiMentionsContentDraft:';

// Single source of truth for the field limits - the backend's pydantic models,
// the admin's yup schema and parseAiMentionsContentValues all agree on these.
export const AI_MENTIONS_LIMITS = {
    title: 200,
    subtitle: 300,
    ctaPrimaryLabel: 40,
    ctaSecondaryLabel: 40,
    prefill: 300,
    whyNowTitle: 120,
    whyNowBody: 600,
    twoMinutesTitle: 120,
    twoMinutesBody: 600,
    twoMinutesCaption: 300,
    stepTitle: 80,
    stepBody: 300,
    stepsClosing: 200,
    proofTitle: 120,
    proofBody: 600,
    finalCtaTitle: 160,
    finalCtaSubtitle: 200,
    finalCtaButtonLabel: 40,
    finalCtaLinkText: 160,
    chatDemoQuestion: 200,
    // the RAW authored string, emphasis markers included
    chatDemoAnswer: 500,
};

// The design's numbered rail is three wide - not a list the admin can grow.
export const AI_MENTIONS_STEP_COUNT = 3;

// wa.me form: 972, a leading 5 mobile prefix, then the 8 subscriber digits.
export const AI_MENTIONS_NUMBER_PATTERN = /^9725\d{8}$/;

// Same cache trap as the homepage document - see siteContentFreshUrl.
export const aiMentionsContentFreshUrl = () => `${AI_MENTIONS_CONTENT_URL}?_=${Date.now()}`;

const isFilledString = (value: any): value is string =>
    typeof value === 'string' && value.trim().length > 0;

// Shallow per-section validation: a malformed section is dropped whole and the
// default for that section is kept, so a bad payload can never blank the hero.
const validHero = (hero: any): AiMentionsHeroContent | null =>
    hero && typeof hero === 'object'
    && isFilledString(hero.title)
    && isFilledString(hero.subtitle)
    && isFilledString(hero.ctaPrimaryLabel)
    && isFilledString(hero.ctaSecondaryLabel)
        ? {
            title: hero.title,
            subtitle: hero.subtitle,
            ctaPrimaryLabel: hero.ctaPrimaryLabel,
            ctaSecondaryLabel: hero.ctaSecondaryLabel
        }
        : null;

// A number that does not match the pattern would build a dead wa.me link, so
// the whole section falls back to the default rather than shipping one.
const validWhatsapp = (whatsapp: any): AiMentionsWhatsappContent | null =>
    whatsapp && typeof whatsapp === 'object'
    && isFilledString(whatsapp.number)
    && AI_MENTIONS_NUMBER_PATTERN.test(whatsapp.number.trim())
    && isFilledString(whatsapp.prefill)
        ? {number: whatsapp.number.trim(), prefill: whatsapp.prefill}
        : null;

const validProse = (value: any): AiMentionsProseContent | null =>
    value && typeof value === 'object' && isFilledString(value.title) && isFilledString(value.body)
        ? {title: value.title, body: value.body}
        : null;

const validTwoMinutes = (value: any): AiMentionsTwoMinutesContent | null => {
    const prose = validProse(value);
    return prose && isFilledString(value.caption) ? {...prose, caption: value.caption} : null;
};

const validSteps = (value: any): AiMentionsStepContent[] | null =>
    Array.isArray(value)
    && value.length === AI_MENTIONS_STEP_COUNT
    && value.every(step => step && typeof step === 'object' && isFilledString(step.title) && isFilledString(step.body))
        ? value.map(step => ({title: step.title, body: step.body}))
        : null;

const validFinalCta = (value: any): AiMentionsFinalCtaContent | null =>
    value && typeof value === 'object'
    && isFilledString(value.title)
    && isFilledString(value.subtitle)
    && isFilledString(value.buttonLabel)
    && isFilledString(value.linkText)
        ? {
            title: value.title,
            subtitle: value.subtitle,
            buttonLabel: value.buttonLabel,
            linkText: value.linkText
        }
        : null;

const validChatDemo = (value: any): AiMentionsChatDemoContent | null =>
    value && typeof value === 'object'
    && isFilledString(value.question)
    && isFilledString(value.answer)
        ? {question: value.question, answer: value.answer}
        : null;

// BACK-COMPAT: a document published before the emphasis moved inline carries it
// in its own `highlight` field. Rebuild the markers around its first occurrence
// so the stored copy keeps rendering highlighted until an admin saves the new
// shape. An answer that already carries a marker is left exactly as authored.
const withInlineMarkers = (chatDemo: any): any => {
    if (!chatDemo || typeof chatDemo !== 'object'
        || !isFilledString(chatDemo.answer) || !isFilledString(chatDemo.highlight)
        || chatDemo.answer.includes('*')) {
        return chatDemo;
    }

    const at = chatDemo.answer.indexOf(chatDemo.highlight);
    if (at < 0) {
        return chatDemo;
    }

    const {answer, highlight} = chatDemo;
    return {
        ...chatDemo,
        answer: `${answer.slice(0, at)}*${highlight}*${answer.slice(at + highlight.length)}`,
    };
};

// Merges section-whole over the defaults - never field by field. A section the
// stored document predates is simply absent, which reads the same as a broken
// one: the shipped copy.
export const mergeAiMentionsContent = (incoming: any): AiMentionsContentValues => ({
    hero: validHero(incoming?.hero) ?? aiMentionsContentDefaults.hero,
    whatsapp: validWhatsapp(incoming?.whatsapp) ?? aiMentionsContentDefaults.whatsapp,
    whyNow: validProse(incoming?.whyNow) ?? aiMentionsContentDefaults.whyNow,
    twoMinutes: validTwoMinutes(incoming?.twoMinutes) ?? aiMentionsContentDefaults.twoMinutes,
    steps: validSteps(incoming?.steps) ?? aiMentionsContentDefaults.steps,
    stepsClosing: isFilledString(incoming?.stepsClosing)
        ? incoming.stepsClosing
        : aiMentionsContentDefaults.stepsClosing,
    proof: validProse(incoming?.proof) ?? aiMentionsContentDefaults.proof,
    finalCta: validFinalCta(incoming?.finalCta) ?? aiMentionsContentDefaults.finalCta,
    chatDemo: validChatDemo(withInlineMarkers(incoming?.chatDemo)) ?? aiMentionsContentDefaults.chatDemo,
});

// Returns the TRIMMED value - the backend strips whitespace before enforcing its
// limits, and returning the raw value would let whitespace-padded strings of any
// size through the "bounded" check.
const boundedString = (value: any, limit: number): string | null => {
    if (!isFilledString(value)) return null;
    const trimmed = value.trim();
    return trimmed.length <= limit ? trimmed : null;
};

const LIMITS = AI_MENTIONS_LIMITS;

const parseProse = (value: any, titleLimit: number, bodyLimit: number): AiMentionsProseContent | null => {
    if (!value || typeof value !== 'object') return null;
    const title = boundedString(value.title, titleLimit);
    const body = boundedString(value.body, bodyLimit);
    return title && body ? {title, body} : null;
};

const parseTwoMinutes = (value: any): AiMentionsTwoMinutesContent | null => {
    const prose = parseProse(value, LIMITS.twoMinutesTitle, LIMITS.twoMinutesBody);
    const caption = boundedString(value?.caption, LIMITS.twoMinutesCaption);
    return prose && caption ? {...prose, caption} : null;
};

const parseSteps = (value: any): AiMentionsStepContent[] | null => {
    if (!Array.isArray(value) || value.length !== AI_MENTIONS_STEP_COUNT) return null;

    const steps = value.map(step => {
        if (!step || typeof step !== 'object') return null;
        const title = boundedString(step.title, LIMITS.stepTitle);
        const body = boundedString(step.body, LIMITS.stepBody);
        return title && body ? {title, body} : null;
    });

    return steps.every(step => step !== null) ? steps as AiMentionsStepContent[] : null;
};

const parseFinalCta = (value: any): AiMentionsFinalCtaContent | null => {
    if (!value || typeof value !== 'object') return null;
    const title = boundedString(value.title, LIMITS.finalCtaTitle);
    const subtitle = boundedString(value.subtitle, LIMITS.finalCtaSubtitle);
    const buttonLabel = boundedString(value.buttonLabel, LIMITS.finalCtaButtonLabel);
    const linkText = boundedString(value.linkText, LIMITS.finalCtaLinkText);
    return title && subtitle && buttonLabel && linkText ? {title, subtitle, buttonLabel, linkText} : null;
};

const parseChatDemo = (value: any): AiMentionsChatDemoContent | null => {
    if (!value || typeof value !== 'object') return null;
    // a draft stashed before the emphasis moved inline still carries it in a
    // `highlight` field - rebuild the markers so the preview keeps its bold.
    // If the two added marker characters would push the answer past the limit,
    // fall back to the unconverted answer (bold lost, draft kept).
    const converted = withInlineMarkers(value);
    const source = typeof converted.answer === 'string'
        && converted.answer.trim().length <= LIMITS.chatDemoAnswer ? converted : value;

    const question = boundedString(source.question, LIMITS.chatDemoQuestion);
    // the limit is on the RAW string - the markers count against it, exactly as
    // they do in the backend's pydantic model
    const answer = boundedString(source.answer, LIMITS.chatDemoAnswer);
    if (!question || !answer) return null;

    return {question, answer};
};

// A section the stored document predates is ABSENT, not broken: it reads as the
// shipped default so the admin can still open and republish a pre-v2 document.
// A section that IS there and does not hold up is a genuine error and fails the
// whole parse, exactly like a malformed hero.
const optionalSection = <T>(value: unknown, parse: (input: any) => T | null, fallback: T): T | null =>
    value === undefined || value === null ? fallback : parse(value);

// STRICT, all-or-nothing validation of an untrusted values object. Used for
// anything that never went through the backend's validators - i.e. the
// localStorage preview drafts, and the admin's own values before it opens one.
export const parseAiMentionsContentValues = (input: unknown): AiMentionsContentValues | null => {
    const raw = input as any;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const {hero, whatsapp} = raw;
    if (!hero || typeof hero !== 'object' || !whatsapp || typeof whatsapp !== 'object') {
        return null;
    }

    const title = boundedString(hero.title, LIMITS.title);
    const subtitle = boundedString(hero.subtitle, LIMITS.subtitle);
    const ctaPrimaryLabel = boundedString(hero.ctaPrimaryLabel, LIMITS.ctaPrimaryLabel);
    const ctaSecondaryLabel = boundedString(hero.ctaSecondaryLabel, LIMITS.ctaSecondaryLabel);
    const prefill = boundedString(whatsapp.prefill, LIMITS.prefill);
    const number = isFilledString(whatsapp.number) ? whatsapp.number.trim() : null;

    if (!title || !subtitle || !ctaPrimaryLabel || !ctaSecondaryLabel || !prefill || !number) {
        return null;
    }
    if (!AI_MENTIONS_NUMBER_PATTERN.test(number)) {
        return null;
    }

    const defaults = aiMentionsContentDefaults;
    const whyNow = optionalSection(raw.whyNow,
        value => parseProse(value, LIMITS.whyNowTitle, LIMITS.whyNowBody), defaults.whyNow);
    const twoMinutes = optionalSection(raw.twoMinutes, parseTwoMinutes, defaults.twoMinutes);
    const steps = optionalSection(raw.steps, parseSteps, defaults.steps);
    const stepsClosing = optionalSection(raw.stepsClosing,
        value => boundedString(value, LIMITS.stepsClosing), defaults.stepsClosing);
    const proof = optionalSection(raw.proof,
        value => parseProse(value, LIMITS.proofTitle, LIMITS.proofBody), defaults.proof);
    const finalCta = optionalSection(raw.finalCta, parseFinalCta, defaults.finalCta);
    const chatDemo = optionalSection(raw.chatDemo, parseChatDemo, defaults.chatDemo);

    if (!whyNow || !twoMinutes || !steps || !stepsClosing || !proof || !finalCta || !chatDemo) {
        return null;
    }

    return {
        hero: {title, subtitle, ctaPrimaryLabel, ctaSecondaryLabel},
        whatsapp: {number, prefill},
        whyNow,
        twoMinutes,
        steps,
        stepsClosing,
        proof,
        finalCta,
        chatDemo,
    };
};

export const aiMentionsDraftStore = createContentDraftStore<AiMentionsContentValues>(
    AI_MENTIONS_CONTENT_TYPE,
    AI_MENTIONS_DRAFT_PREFIX,
    parseAiMentionsContentValues,
);

export type AiMentionsContentState = RevisionedContentState<AiMentionsContentValues>;

const useAiMentionsContent = (): AiMentionsContentState =>
    useRevisionedContent<AiMentionsContentValues>({
        url: AI_MENTIONS_CONTENT_URL,
        defaults: aiMentionsContentDefaults,
        mergeContent: mergeAiMentionsContent,
        draftStore: aiMentionsDraftStore,
    });

export default useAiMentionsContent;
